import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  hourWindowMinutes,
  isPermissionCancelable,
  type PermissionRequest,
} from '@domain/entities/PermissionRequest';
import { reasonsForScope } from '@domain/entities/RuntimeConfig';
import { countBusinessDays, NO_HOLIDAYS } from '@domain/policies/BusinessDayPolicy';
import { DayOfWeek } from '@domain/value-objects/CalendarDate';
import { PERMISSION_REQUEST_STATUSES } from '@domain/vocabulary';
import { getServices } from '@infrastructure/services';
import {
  Banner,
  Button,
  Card,
  Chip,
  EmptyState,
  Field,
  Modal,
  Spinner,
  cssVars,
  cx,
  Pagination,
} from '../../design-system';
import { usePagination } from '../../hooks/usePagination';
import { formatDateRange, formatHourWindow, permissionStatusColors } from '../../format';
import { useAbsenceScope } from '../../hooks/useAbsenceScope';
import { useAccess } from '../../hooks/useAccess';
import { useSession } from '../../providers/SessionProvider';
import { useToast } from '../../providers/ToastProvider';
import shared from '../shared.module.css';
import { PermissionWizard } from './PermissionWizard';
import styles from './AbsencesScreens.module.css';
import { loadFailed } from '../../loadError';
import { ReasonPicker } from '../ReasonPicker';
import { PageTitle } from '../../shell/PageTitle';

/**
 * Home office y permisos — transcribed from
 * `design/reference/screens/home-office-permisos.html`.
 *
 * A separate flow from vacation rather than a variant of it, because the rules
 * genuinely differ: these do not consume the vacation balance, they may be
 * measured in hours, they may be unpaid, and they need one approval rather than
 * two. The screen is teal throughout — that colour is what tells it apart from
 * the amber incapacity screen at a glance.
 */

/**
 * The filter row, transcribed from the prototype's `permFilters`.
 *
 * It mixes two axes on purpose — two statuses and the two commonest kinds —
 * because that is how the list is actually scanned: "what needs me" and "who is
 * remote". The kinds come from the live catalogue, so a renamed kind renames its
 * filter with no redeploy.
 */
const buildFilters = (kinds: readonly string[]): readonly string[] => [
  'Todos',
  'Por aprobar',
  ...kinds.slice(0, 2),
  'Aprobado',
];

/**
 * A colour and glyph per kind of permission.
 *
 * The catalogue is runtime-editable, so an unrecognised kind must still render —
 * it falls back to the neutral accent rather than throwing or rendering blank.
 */
const KIND_STYLE: Readonly<Record<string, { color: string; tint: string; glyph: string }>> = {
  'Home office': { color: '#12a594', tint: '#dff6f1', glyph: '⌂' },
  'Día personal': { color: '#7c5cff', tint: '#efebff', glyph: '☺' },
  'Permiso por horas': { color: '#e8930c', tint: '#fff3da', glyph: '◷' },
  'Permiso sin goce': { color: '#64748b', tint: '#eef1f6', glyph: '◌' },
  Duelo: { color: '#4a6fa5', tint: '#eaeffb', glyph: '❀' },
};

const kindStyle = (kind: string): { color: string; tint: string; glyph: string } =>
  KIND_STYLE[kind] ?? { color: '#64748b', tint: '#eef1f6', glyph: '·' };

/** Monday to Friday, paired with the weekday index `CalendarDate` reports. */
const WEEKDAYS = [
  { label: 'Lun', dayOfWeek: DayOfWeek.Monday },
  { label: 'Mar', dayOfWeek: DayOfWeek.Tuesday },
  { label: 'Mié', dayOfWeek: DayOfWeek.Wednesday },
  { label: 'Jue', dayOfWeek: DayOfWeek.Thursday },
  { label: 'Vie', dayOfWeek: DayOfWeek.Friday },
] as const;

export const PermissionsScreen = (): ReactNode => {
  const session = useSession();
  const { holds, featureOn } = useAccess();
  const absences = useAbsenceScope();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<string>('Todos');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [cancelling, setCancelling] = useState<PermissionRequest | null>(null);
  const today = getServices().clock.today();

  const canRequest = holds('solicitar');
  /*
   * The list is the viewer's own, so the note says so plainly. Deciding on a
   * colleague's permit happens in Aprobaciones — it never belonged on a screen
   * where the viewer's own permit is one of the rows, because the buttons would
   * have appeared on it.
   */
  const scopeNote = 'Solo tus registros';

  /** Denominator for the week table — how many people the list covers. */
  const activePeople = useQuery({
    queryKey: ['home', 'activePeople', session.areaId],
    queryFn: async () => {
      const result = await getServices().people.countByStatus(session.areaId, 'active');
      return result.ok ? result.value : 0;
    },
  });

  const permissions = useQuery({
    queryKey: ['permissions', absences.scope],
    queryFn: async () => {
      const result = await getServices().permissions.list(absences.scope, { limit: 50 });
      if (!result.ok) throw loadFailed('No pudimos cargar los permisos.', result.error);
      return result.value.items;
    },
  });

  const all = permissions.data ?? [];
  // One filter, two axes: it matches either a status or a kind.
  const visible = all.filter(
    (p) => filter === 'Todos' || p.status === filter || p.kind === filter,
  );
  /* De diez en diez: los permisos de un área se acumulan todo el año. */
  const pager = usePagination(visible, filter);

  if (!featureOn('permisos')) {
    return (
      <Card title="Home office y permisos">
        <Banner tone="info">
          El módulo de permisos está apagado. Enciéndelo en Features y módulos.
        </Banner>
        {/*
          El aviso de arriba viene del prototipo y manda a la pantalla global, que
          ya no es el único sitio: un área puede encenderlo para sí sola aunque la
          empresa lo tenga apagado. Sin esta segunda línea, quien administra un
          área lee «enciéndelo en Features y módulos», descubre que no tiene ese
          permiso, y se queda sin saber que sí tiene el suyo.
        */}
        <Banner tone="info">
          También puede encenderse sólo para un área, desde Incapacidades y permisos,
          aunque la empresa lo tenga apagado.
        </Banner>
      </Card>
    );
  }


  const headcount = activePeople.data ?? 0;

  const filters = buildFilters(
    session.config.catalogs.permissionKinds.filter((k) => k.enabled).map((k) => k.id),
  );

  /*
   * Transcribed from the prototype's `permKpis`.
   *
   * «Días remotos» counts business *days*, not requests — a five-day remote week
   * and a single remote Friday are not the same load on the office, and counting
   * requests would say they are.
   */
  const kpis: readonly {
    label: string;
    value: number;
    chip: string;
    note: string;
    color: string;
  }[] = [
    {
      label: 'Días remotos',
      value: all
        .filter((p) => p.kind === 'Home office' && p.status === 'Aprobado')
        .reduce((sum, p) => sum + countBusinessDays(p.range, NO_HOLIDAYS), 0),
      chip: 'Remoto',
      note: 'aprobados en el periodo',
      color: 'var(--color-success)',
    },
    {
      label: 'Por aprobar',
      value: all.filter((p) => p.status === 'Por aprobar').length,
      chip: 'Pendiente',
      note: 'esperan al líder',
      color: 'var(--color-warning)',
    },
    {
      label: 'Días personales',
      value: all.filter((p) => p.kind === 'Día personal').length,
      chip: 'Cuota',
      note: 'de 4 al año por persona',
      color: 'var(--color-brand)',
    },
    {
      label: 'Sin goce',
      value: all.filter((p) => !p.withPay).length,
      chip: 'Nómina',
      note: 'descuento aplicado en nómina',
      color: 'var(--color-violet)',
    },
  ];

  /**
   * Office presence per weekday.
   *
   * Stated as people *in* the office out of the headcount, which is the way the
   * design frames it and the way the question is actually asked — "will there be
   * anyone there on Friday". Derived from the approved home-office days rather
   * than from a declared policy, so it reflects what is really booked.
   */
  const weekPlan = WEEKDAYS.map(({ label, dayOfWeek }) => {
    const remote = all.filter(
      (p) =>
        p.kind === 'Home office' &&
        p.status === 'Aprobado' &&
        p.range.toDayArray().some((day) => day.dayOfWeek === dayOfWeek),
    ).length;
    const inOffice = Math.max(0, headcount - remote);
    return {
      label,
      inOffice,
      // Three bands, matching the design: everyone in, most in, most out.
      mode: remote === 0 ? 'Oficina' : inOffice * 2 > headcount ? 'Híbrido' : 'Remoto',
    };
  });

  /**
   * What each kind allows, read from the live catalogue.
   *
   * The notice period closes the list because it is the one rule that applies
   * to every kind, and it is the one people most often miss.
   */
  const permitRules: readonly { label: string; value: string }[] = [
    ...session.config.catalogs.permissionKinds
      .filter((k) => k.enabled)
      .map((k) => ({ label: k.id, value: k.note })),
    {
      label: 'Aviso mínimo',
      value: `${String(session.config.rules.noticeDays)} ${
        session.config.rules.noticeDays === 1 ? 'día' : 'días'
      }`,
    },
  ];

  return (
    <div className="flow">
      <header className={styles.header}>
        <div>
          <PageTitle className={styles.title}>Home office y permisos</PageTitle>
          <p className={styles.note}>
            {scopeNote} · los permisos con goce no descuentan días de vacaciones.
          </p>
        </div>

        {canRequest && (
          <button
            type="button"
            className={styles.cta}
            onClick={() => {
              setWizardOpen(true);
            }}
          >
            <span className={styles.ctaPlus} aria-hidden="true">
              +
            </span>{' '}
            Nuevo permiso
          </button>
        )}
      </header>

      {wizardOpen && (
        <PermissionWizard
          onClose={() => {
            setWizardOpen(false);
          }}
          onSubmitted={async () => {
            // The sidebar pill and the home inbox read the same documents, so
            // they go stale together if only this list is refreshed.
            await queryClient.invalidateQueries({ queryKey: ['permissions'] });
            await queryClient.invalidateQueries({ queryKey: ['home'] });
          }}
        />
      )}

      {cancelling !== null && (
        <CancelPermitDialog
          permission={cancelling}
          onClose={() => {
            setCancelling(null);
          }}
          onDone={async () => {
            setCancelling(null);
            // The leader's board and the sidebar pill read the same document.
            await queryClient.invalidateQueries({ queryKey: ['permissions'] });
            await queryClient.invalidateQueries({ queryKey: ['approvals'] });
            await queryClient.invalidateQueries({ queryKey: ['home'] });
            await queryClient.invalidateQueries({ queryKey: ['shell', 'badge'] });
          }}
        />
      )}

      <div className={styles.kpis}>
        {kpis.map((kpi) => (
          <div key={kpi.label} className={styles.kpi} style={cssVars({ '--kpi-color': kpi.color })}>
            <div className={styles.kpiHead}>
              <span className={styles.kpiLabel}>{kpi.label}</span>
              <Chip background="var(--kpi-tint)" foreground={kpi.color}>
                {kpi.chip}
              </Chip>
            </div>
            <div className={styles.kpiValue}>{kpi.value}</div>
            <div className={styles.kpiNote}>{kpi.note}</div>
          </div>
        ))}
      </div>

      <div className={styles.split}>
        <div>
          <div className={cx(styles.filters, 'scroll-x')} role="group" aria-label="Filtrar permisos">
            {filters.map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={filter === option}
                className={cx(styles.filter, filter === option && styles.filterActive)}
                onClick={() => {
                  setFilter(option);
                }}
              >
                {option}
              </button>
            ))}
          </div>

          {permissions.isLoading ? (
            <Card>
              <Spinner label="Cargando" />
            </Card>
          ) : permissions.error !== null ? (
            <Banner tone="danger">No pudimos cargar los permisos.</Banner>
          ) : visible.length === 0 ? (
            <Card>
              <EmptyState
                glyph="⌂"
                title="Nada por aquí"
                note="Los permisos de home office, días personales y horas aparecerán en esta lista."
              />
            </Card>
          ) : (
            <ul className={styles.list}>
              {pager.items.map((permission) => {
                const colors = permissionStatusColors(permission.status);
                const kind = kindStyle(permission.kind);
                const minutes =
                  permission.hours === null ? 0 : hourWindowMinutes(permission.hours);

                return (
                  <li
                    key={permission.id}
                    className={styles.permitRow}
                    style={cssVars({ '--row-accent': kind.color, '--row-tint': kind.tint })}
                  >
                    <span className={styles.permitIcon} aria-hidden="true">
                      {kind.glyph}
                    </span>

                    <div>
                      <div className={styles.permitHeadline}>
                        <span className={styles.permitName}>{permission.personName}</span>
                        <Chip background={kind.tint} foreground={kind.color}>
                          {permission.kind}
                        </Chip>
                      </div>
                      <div className={styles.permitMeta}>
                        {formatDateRange(permission.range)}
                        {permission.hours !== null &&
                          ` · ${formatHourWindow(permission.hours)}${
                            /* Las horas sólo se escriben cuando se pueden contar:
                               un permiso sin regreso no tiene duración conocida y
                               un «(0 h)» ahí se leería como que no faltó. */
                            permission.hours.to === null
                              ? ''
                              : ` (${String(Math.round(minutes / 60))} h)`
                          }`}{' '}
                        · {permission.subAreaName} · {permission.folio}
                      </div>
                      <p className={styles.permitReason}>{permission.reason}</p>
                      {permission.resolutionReason !== null && (
                        <p className={shared.rowReason}>{permission.resolutionReason}</p>
                      )}
                    </div>

                    <div className={styles.permitSide}>
                      <Chip background={colors.background} foreground={colors.foreground}>
                        {permission.status}
                      </Chip>

                      <span className={styles.permitPay}>
                        {permission.withPay ? 'Con goce de sueldo' : 'Sin goce de sueldo'}
                      </span>

                      {/* Own permits only. The list is already scoped to the
                          viewer, but the button carries an authority claim and
                          those are worth stating twice. */}
                      {permission.personId === session.personId &&
                        isPermissionCancelable(permission, today) && (
                          <button
                            type="button"
                            className={styles.permitCancel}
                            onClick={() => {
                              setCancelling(permission);
                            }}
                          >
                            Cancelar
                          </button>
                        )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <Pagination pager={pager} noun="permisos" detached />
        </div>

        <aside className={styles.aside}>
          <section className={styles.panel}>
            <h2 className={styles.panelTitle}>Esquema de la semana</h2>
            <p className={styles.panelNote}>Cuánta gente queda en oficina cada día.</p>
            <div className={styles.week}>
              {weekPlan.map((day) => (
                <div key={day.label} className={styles.weekRow}>
                  <span className={styles.weekDay}>{day.label}</span>
                  <div
                    className={styles.weekBar}
                    style={cssVars(
                      day.mode === 'Remoto'
                        ? { '--week-bg': '#dff6f1', '--week-fg': '#0b7466' }
                        : {
                            '--week-bg':
                              'linear-gradient(90deg, var(--color-brand), var(--color-brand-darkest))',
                            '--week-fg': '#fff',
                          },
                    )}
                  >
                    {day.mode}
                  </div>
                  <span className={styles.weekCount}>
                    {day.inOffice} / {headcount}
                  </span>
                </div>
              ))}
            </div>
          </section>

          <section className={styles.panel}>
            <h2 className={styles.panelTitle}>Tipos de permiso</h2>
            {/* The rules, not the tallies. Someone reading this column is about
                to file or judge a permit and needs to know what each kind
                allows — the counts are already in the KPI band above. */}
            <div className={styles.definitionList}>
              {permitRules.map((row) => (
                <div key={row.label} className={styles.definitionRow}>
                  <span className={styles.definitionKey}>{row.label}</span>
                  <strong className={styles.definitionValue}>{row.value}</strong>
                </div>
              ))}
            </div>
          </section>
        </aside>
      </div>

    </div>
  );
};

export const PERMISSION_STATUS_OPTIONS = PERMISSION_REQUEST_STATUSES;

/**
 * Withdrawing your own permit.
 *
 * Deliberately the same shape as the vacation cancellation: same catalogue, same
 * mandatory reason, same wording on the buttons. Withdrawing a Thursday of home
 * office and withdrawing a week of holiday are the same act to the person doing
 * it, and giving them two different dialogues would only make one of them look
 * like something else.
 */
const CancelPermitDialog = ({
  permission,
  onClose,
  onDone,
}: {
  permission: PermissionRequest;
  onClose: () => void;
  onDone: () => Promise<void>;
}): ReactNode => {
  const session = useSession();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const reasons = reasonsForScope(session.config.catalogs, 'cancelacion');

  const submit = async (): Promise<void> => {
    if (reason.length === 0) return;
    setBusy(true);

    const result = await getServices().permissions.cancel(permission.id, reason);
    setBusy(false);

    if (!result.ok) {
      toast.bad(
        result.error.kind === 'unavailable'
          ? 'No se pudo cancelar. Revisa tu conexión.'
          : 'No se pudo cancelar ese permiso.',
      );
      return;
    }

    toast.ok(`${permission.folio} quedó cancelado.`);
    await onDone();
  };

  return (
    <Modal
      title={`Cancelar ${permission.folio}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Conservar
          </Button>
          <Button
            variant="danger"
            busy={busy}
            disabled={reason.length === 0}
            onClick={() => {
              void submit();
            }}
          >
            Cancelar el permiso
          </Button>
        </>
      }
    >
      <div className={shared.stack}>
        <p className={shared.rowNote}>
          Vas a cancelar tu {permission.kind.toLowerCase()} del{' '}
          {formatDateRange(permission.range)}.{' '}
          {permission.status === 'Aprobado'
            ? 'Ya estaba aprobado, así que tu líder verá que lo retiraste y por qué.'
            : 'Sale de la bandeja de tu líder y deja de esperar decisión.'}
        </p>

        <Field label="Motivo de la cancelación">
          {(id) => (
            <ReasonPicker id={id} reasons={reasons} value={reason} onChange={setReason} />
          )}
        </Field>
      </div>
    </Modal>
  );
};
